import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '@/components/PageHeader';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { 
  Search, 
  Filter, 
  Download, 
  ChevronLeft, 
  ChevronRight,
  ArrowUpDown,
  Info,
  X,
  Plus,
  Building,
  Calendar,
  CreditCard,
  DollarSign,
  FileText,
  MapPin,
  Zap,
  Receipt,
  Eye,
  CheckCircle,
  Wallet,
  Clock
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { format } from 'date-fns';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar as CalendarPicker } from '@/components/ui/calendar';
import { Checkbox } from '@/components/ui/checkbox';
import { Separator } from '@/components/ui/separator';
import { generateDummyBill } from './dummyBillData';

interface BillFilters {
  vendors: string[];
  utilityType: string;
  location: string;
  state: string;
  costMin: number;
  costMax: number;
  invoiceDateFrom: Date | null;
  invoiceDateTo: Date | null;
  paymentMarkedFrom: Date | null;
  paymentMarkedTo: Date | null;
  locationAttribute: string;
  attributeValue: string;
  billingLineItem: string;
  mostRecentPerAccount: boolean;
  includeDeleted: boolean;
}

export default function TumBillsList() {
  const navigate = useNavigate();
  const { currentWorkspace } = useWorkspace();
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedBills, setSelectedBills] = useState<Set<string>>(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [sortField, setSortField] = useState<string>('bill_date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  const [filters, setFilters] = useState<BillFilters>({
    vendors: [],
    utilityType: '',
    location: '',
    state: '',
    costMin: 0,
    costMax: 0,
    invoiceDateFrom: null,
    invoiceDateTo: null,
    paymentMarkedFrom: null,
    paymentMarkedTo: null,
    locationAttribute: '',
    attributeValue: '',
    billingLineItem: '',
    mostRecentPerAccount: false,
    includeDeleted: false,
  });

  // Fetch vendors for filter
  const { data: vendors } = useQuery({
    queryKey: ['tum-vendors', currentWorkspace?.id],
    queryFn: async () => {
      if (!currentWorkspace?.id) return [];
      const { data, error } = await supabase
        .from('tum_utility_vendors')
        .select('id, vendor_name')
        .eq('workspace_id', currentWorkspace.id)
        .order('vendor_name', { ascending: true });
      if (error) throw error;
      return data || [];
    },
    enabled: !!currentWorkspace?.id,
  });

  // Fetch properties for location filter
  const { data: properties } = useQuery({
    queryKey: ['tum-properties', currentWorkspace?.id],
    queryFn: async () => {
      if (!currentWorkspace?.id) return [];
      const { data, error } = await supabase
        .from('tum_properties')
        .select('id, property_name')
        .eq('workspace_id', currentWorkspace.id)
        .order('property_name', { ascending: true });
      if (error) throw error;
      return data || [];
    },
    enabled: !!currentWorkspace?.id,
  });

  // Fetch bills with filters
  const { data: billsData, isLoading } = useQuery({
    queryKey: [
      'tum-bills-list',
      currentWorkspace?.id,
      filters,
      searchTerm,
      currentPage,
      pageSize,
      sortField,
      sortDirection,
    ],
    queryFn: async () => {
      if (!currentWorkspace?.id) return { bills: [], total: 0 };

      let query = supabase
        .from('bills')
        .select(`
          *,
          property:tum_properties(id, property_name),
          utility_account:tum_utility_accounts(
            id,
            account_number,
            commodity,
            vendor:tum_utility_vendors(id, vendor_name)
          )
        `, { count: 'exact' })
        .eq('workspace_id', currentWorkspace.id);

      // Apply filters
      if (filters.vendors.length > 0) {
        // Filter by vendor through utility_account
        // This would need a join or subquery
      }

      if (filters.utilityType) {
        query = query.eq('utility_account.commodity', filters.utilityType);
      }

      if (filters.location) {
        query = query.eq('tum_property_id', filters.location);
      }

      if (filters.costMin > 0) {
        query = query.gte('amount', filters.costMin);
      }

      if (filters.costMax > 0) {
        query = query.lte('amount', filters.costMax);
      }

      if (filters.invoiceDateFrom) {
        query = query.gte('bill_date', filters.invoiceDateFrom.toISOString().split('T')[0]);
      }

      if (filters.invoiceDateTo) {
        query = query.lte('bill_date', filters.invoiceDateTo.toISOString().split('T')[0]);
      }

      if (!filters.includeDeleted) {
        // Assuming there's a deleted_at or status field
        // query = query.is('deleted_at', null);
      }

      // Search
      if (searchTerm) {
        query = query.or(`bill_number.ilike.%${searchTerm}%,invoice_number.ilike.%${searchTerm}%`);
      }

      // Sorting
      const orderField = sortField === 'vendor' ? 'utility_account.vendor.vendor_name' : sortField;
      query = query.order(orderField, { ascending: sortDirection === 'asc' });

      // Pagination
      const from = (currentPage - 1) * pageSize;
      const to = from + pageSize - 1;
      query = query.range(from, to);

      const { data, error, count } = await query;
      if (error) throw error;

      return { bills: data || [], total: count || 0 };
    },
    enabled: !!currentWorkspace?.id,
  });

  // Transform dummy bill to match the list structure
  const dummyBillForList = useMemo(() => {
    const dummyBill = generateDummyBill();
    return {
      id: 'demo-1096901', // Use a special ID for the demo bill
      bill_number: dummyBill.bill_number,
      invoice_number: dummyBill.invoice_number,
      bill_date: dummyBill.bill_date,
      due_date: dummyBill.due_date,
      billing_period_start: dummyBill.billing_period_start,
      billing_period_end: dummyBill.billing_period_end,
      amount: dummyBill.amount,
      amount_due: dummyBill.amount_due,
      usage_amount: dummyBill.usage_amount,
      usage_unit: dummyBill.usage_unit,
      property: dummyBill.property,
      utility_account: {
        id: dummyBill.utility_account.id,
        account_number: dummyBill.utility_account.account_number,
        commodity: dummyBill.utility_account.commodity,
        vendor: dummyBill.utility_account.vendor,
      },
      unit: dummyBill.unit,
      workspace_id: dummyBill.workspace_id,
      created_at: dummyBill.created_at,
      // Add a flag to identify this as a demo bill
      _isDemo: true,
    };
  }, []);

  // Combine real bills with dummy bill
  const allBills = useMemo(() => {
    const realBills = billsData?.bills || [];
    
    // Check if search term matches dummy bill
    const searchMatchesDummy = searchTerm && (
      dummyBillForList.bill_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (dummyBillForList.invoice_number?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
      dummyBillForList.utility_account?.account_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      dummyBillForList.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      '1096901'.includes(searchTerm) ||
      'demo'.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // If there's a search term and it matches dummy bill, include it
    // Or if no search term, include it at the beginning
    if (!searchTerm || searchMatchesDummy) {
      // Check if dummy bill already exists in the list (avoid duplicates)
      const dummyExists = realBills.some((b: any) => 
        b.id === dummyBillForList.id || 
        b.bill_number === dummyBillForList.bill_number ||
        b._isDemo
      );
      if (!dummyExists) {
        return [dummyBillForList, ...realBills];
      }
    }
    
    return realBills;
  }, [billsData?.bills, searchTerm, dummyBillForList]);

  const bills = allBills;
  const hasDummyBill = bills.some((b: any) => b._isDemo);
  const totalBills = (billsData?.total || 0) + (hasDummyBill ? 1 : 0);
  const totalPages = Math.ceil(totalBills / pageSize);

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const handleSelectAll = () => {
    if (selectedBills.size === bills.length && bills.length > 0) {
      setSelectedBills(new Set());
    } else {
      setSelectedBills(new Set(bills.map((b: any) => b.id)));
    }
  };

  const handleSelectBill = (billId: string) => {
    const newSelected = new Set(selectedBills);
    if (newSelected.has(billId)) {
      newSelected.delete(billId);
    } else {
      newSelected.add(billId);
    }
    setSelectedBills(newSelected);
  };

  const clearFilters = () => {
    setFilters({
      vendors: [],
      utilityType: '',
      location: '',
      state: '',
      costMin: 0,
      costMax: 0,
      invoiceDateFrom: null,
      invoiceDateTo: null,
      paymentMarkedFrom: null,
      paymentMarkedTo: null,
      locationAttribute: '',
      attributeValue: '',
      billingLineItem: '',
      mostRecentPerAccount: false,
      includeDeleted: false,
    });
  };

  const formatCurrency = (amount: number | string) => {
    const num = typeof amount === 'string' ? parseFloat(amount) : amount;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(num || 0);
  };

  const formatDate = (date: string | Date | null) => {
    if (!date) return 'N/A';
    try {
      return format(new Date(date), 'MM/dd/yyyy');
    } catch {
      return 'N/A';
    }
  };

  const SortableHeader = ({ field, children, icon }: { field: string; children: React.ReactNode; icon?: React.ReactNode }) => (
    <TableHead className="cursor-pointer hover:bg-muted/50 transition-colors" onClick={() => handleSort(field)}>
      <div className="flex items-center gap-2 font-semibold">
        {icon && <span className="text-muted-foreground">{icon}</span>}
        {children}
        <ArrowUpDown className="h-3 w-3 text-muted-foreground" />
      </div>
    </TableHead>
  );

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold tracking-tight">Utility Bill Management</h1>
            <p className="text-muted-foreground mt-2 text-lg">
              Comprehensive oversight of utility invoices, payments, and account activity
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm">
              <Download className="h-4 w-4 mr-2" />
              Export
            </Button>
            <Button size="sm">
              <Plus className="h-4 w-4 mr-2" />
              New Bill
            </Button>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <Card className="border-l-4 border-l-primary/50">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Filter className="h-5 w-5 text-primary" />
            </div>
            <div>
              <CardTitle className="text-lg">Search & Filter</CardTitle>
              <CardDescription>Find and filter utility bills by multiple criteria</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="flex items-center gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search for Billing ID, Invoice Number, Service Account ID or other..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 h-11"
              />
            </div>
            <Button
              variant={showFilters ? 'default' : 'outline'}
              onClick={() => setShowFilters(!showFilters)}
              className="h-11 gap-2"
            >
              <Filter className="h-4 w-4" />
              {showFilters ? 'Hide Filters' : 'Show Filters'}
            </Button>
          </div>

          {/* Advanced Filters Panel */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* Vendor Filter */}
                <div className="space-y-2">
                  <Label>Vendor</Label>
                  <div className="space-y-2">
                    {filters.vendors.map((vendorId) => {
                      const vendor = vendors?.find((v: any) => v.id === vendorId);
                      return (
                        <div key={vendorId} className="flex items-center gap-2">
                          <Input value={vendor?.vendor_name || ''} readOnly className="flex-1" />
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setFilters(prev => ({
                                ...prev,
                                vendors: prev.vendors.filter(id => id !== vendorId),
                              }));
                            }}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      );
                    })}
                    <Select
                      value=""
                      onValueChange={(value) => {
                        if (value && !filters.vendors.includes(value)) {
                          setFilters(prev => ({
                            ...prev,
                            vendors: [...prev.vendors, value],
                          }));
                        }
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select vendor" />
                      </SelectTrigger>
                      <SelectContent>
                        {vendors?.map((vendor: any) => (
                          <SelectItem key={vendor.id} value={vendor.id}>
                            {vendor.vendor_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {filters.vendors.length > 0 && (
                      <Button
                        variant="link"
                        size="sm"
                        className="text-xs"
                        onClick={() => {
                          const select = document.createElement('select');
                          // Add another vendor
                        }}
                      >
                        Add another
                      </Button>
                    )}
                  </div>
                </div>

                {/* Utility Type */}
                <div className="space-y-2">
                  <Label>Utility Type</Label>
                  <Select
                    value={filters.utilityType}
                    onValueChange={(value) => setFilters(prev => ({ ...prev, utilityType: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="electricity">Electricity</SelectItem>
                      <SelectItem value="gas">Gas</SelectItem>
                      <SelectItem value="water">Water</SelectItem>
                      <SelectItem value="sewer">Sewer</SelectItem>
                      <SelectItem value="internet">Internet</SelectItem>
                      <SelectItem value="phone">Phone</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Location */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Location</Label>
                    <Button variant="link" size="sm" className="text-xs">
                      Hierarchies
                    </Button>
                  </div>
                  <Select
                    value={filters.location}
                    onValueChange={(value) => setFilters(prev => ({ ...prev, location: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      {properties?.map((property: any) => (
                        <SelectItem key={property.id} value={property.id}>
                          {property.property_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* State */}
                <div className="space-y-2">
                  <Label>State</Label>
                  <Select
                    value={filters.state}
                    onValueChange={(value) => setFilters(prev => ({ ...prev, state: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="AL">Alabama</SelectItem>
                      <SelectItem value="AK">Alaska</SelectItem>
                      <SelectItem value="AZ">Arizona</SelectItem>
                      <SelectItem value="AR">Arkansas</SelectItem>
                      <SelectItem value="CA">California</SelectItem>
                      <SelectItem value="CO">Colorado</SelectItem>
                      <SelectItem value="CT">Connecticut</SelectItem>
                      <SelectItem value="DE">Delaware</SelectItem>
                      <SelectItem value="FL">Florida</SelectItem>
                      <SelectItem value="GA">Georgia</SelectItem>
                      <SelectItem value="HI">Hawaii</SelectItem>
                      <SelectItem value="ID">Idaho</SelectItem>
                      <SelectItem value="IL">Illinois</SelectItem>
                      <SelectItem value="IN">Indiana</SelectItem>
                      <SelectItem value="IA">Iowa</SelectItem>
                      <SelectItem value="KS">Kansas</SelectItem>
                      <SelectItem value="KY">Kentucky</SelectItem>
                      <SelectItem value="LA">Louisiana</SelectItem>
                      <SelectItem value="ME">Maine</SelectItem>
                      <SelectItem value="MD">Maryland</SelectItem>
                      <SelectItem value="MA">Massachusetts</SelectItem>
                      <SelectItem value="MI">Michigan</SelectItem>
                      <SelectItem value="MN">Minnesota</SelectItem>
                      <SelectItem value="MS">Mississippi</SelectItem>
                      <SelectItem value="MO">Missouri</SelectItem>
                      <SelectItem value="MT">Montana</SelectItem>
                      <SelectItem value="NE">Nebraska</SelectItem>
                      <SelectItem value="NV">Nevada</SelectItem>
                      <SelectItem value="NH">New Hampshire</SelectItem>
                      <SelectItem value="NJ">New Jersey</SelectItem>
                      <SelectItem value="NM">New Mexico</SelectItem>
                      <SelectItem value="NY">New York</SelectItem>
                      <SelectItem value="NC">North Carolina</SelectItem>
                      <SelectItem value="ND">North Dakota</SelectItem>
                      <SelectItem value="OH">Ohio</SelectItem>
                      <SelectItem value="OK">Oklahoma</SelectItem>
                      <SelectItem value="OR">Oregon</SelectItem>
                      <SelectItem value="PA">Pennsylvania</SelectItem>
                      <SelectItem value="RI">Rhode Island</SelectItem>
                      <SelectItem value="SC">South Carolina</SelectItem>
                      <SelectItem value="SD">South Dakota</SelectItem>
                      <SelectItem value="TN">Tennessee</SelectItem>
                      <SelectItem value="TX">Texas</SelectItem>
                      <SelectItem value="UT">Utah</SelectItem>
                      <SelectItem value="VT">Vermont</SelectItem>
                      <SelectItem value="VA">Virginia</SelectItem>
                      <SelectItem value="WA">Washington</SelectItem>
                      <SelectItem value="WV">West Virginia</SelectItem>
                      <SelectItem value="WI">Wisconsin</SelectItem>
                      <SelectItem value="WY">Wyoming</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Cost Range */}
                <div className="space-y-2">
                  <Label>Cost Range</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      placeholder="0"
                      value={filters.costMin || ''}
                      onChange={(e) => setFilters(prev => ({ ...prev, costMin: parseFloat(e.target.value) || 0 }))}
                      className="w-full"
                    />
                    <span className="text-muted-foreground">to</span>
                    <Input
                      type="number"
                      placeholder="0"
                      value={filters.costMax || ''}
                      onChange={(e) => setFilters(prev => ({ ...prev, costMax: parseFloat(e.target.value) || 0 }))}
                      className="w-full"
                    />
                  </div>
                </div>

                {/* Invoice Date */}
                <div className="space-y-2">
                  <Label>Invoice Date</Label>
                  <div className="flex items-center gap-2">
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start text-left font-normal">
                          {filters.invoiceDateFrom ? format(filters.invoiceDateFrom, 'MM/dd/yyyy') : 'mm/dd/yyyy'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <CalendarPicker
                          mode="single"
                          selected={filters.invoiceDateFrom || undefined}
                          onSelect={(date: Date | undefined) => setFilters(prev => ({ ...prev, invoiceDateFrom: date || null }))}
                        />
                      </PopoverContent>
                    </Popover>
                    <span className="text-muted-foreground">to</span>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start text-left font-normal">
                          {filters.invoiceDateTo ? format(filters.invoiceDateTo, 'MM/dd/yyyy') : 'mm/dd/yyyy'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <CalendarPicker
                          mode="single"
                          selected={filters.invoiceDateTo || undefined}
                          onSelect={(date: Date | undefined) => setFilters(prev => ({ ...prev, invoiceDateTo: date || null }))}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>

                {/* Date Marked for Payment */}
                <div className="space-y-2">
                  <Label>Date Marked for Payment</Label>
                  <div className="flex items-center gap-2">
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start text-left font-normal">
                          {filters.paymentMarkedFrom ? format(filters.paymentMarkedFrom, 'MM/dd/yyyy') : 'mm/dd/yyyy'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <CalendarPicker
                          mode="single"
                          selected={filters.paymentMarkedFrom || undefined}
                          onSelect={(date: Date | undefined) => setFilters(prev => ({ ...prev, paymentMarkedFrom: date || null }))}
                        />
                      </PopoverContent>
                    </Popover>
                    <span className="text-muted-foreground">to</span>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start text-left font-normal">
                          {filters.paymentMarkedTo ? format(filters.paymentMarkedTo, 'MM/dd/yyyy') : 'mm/dd/yyyy'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <CalendarPicker
                          mode="single"
                          selected={filters.paymentMarkedTo || undefined}
                          onSelect={(date: Date | undefined) => setFilters(prev => ({ ...prev, paymentMarkedTo: date || null }))}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>

                {/* Location Attribute */}
                <div className="space-y-2">
                  <Label>Location Attribute</Label>
                  <Select
                    value={filters.locationAttribute}
                    onValueChange={(value) => setFilters(prev => ({ ...prev, locationAttribute: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="property_type">Property Type</SelectItem>
                      <SelectItem value="building">Building</SelectItem>
                      <SelectItem value="floor">Floor</SelectItem>
                      <SelectItem value="unit">Unit</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Attribute Value */}
                <div className="space-y-2">
                  <Label>Attribute Value</Label>
                  <Select
                    value={filters.attributeValue}
                    onValueChange={(value) => setFilters(prev => ({ ...prev, attributeValue: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      {/* Values would be populated based on locationAttribute */}
                    </SelectContent>
                  </Select>
                </div>

                {/* Billing Line Item Description */}
                <div className="space-y-2">
                  <Label>Billing Line Item Description</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search"
                      value={filters.billingLineItem}
                      onChange={(e) => setFilters(prev => ({ ...prev, billingLineItem: e.target.value }))}
                      className="pl-10"
                    />
                  </div>
                </div>
              </div>

              {/* Toggles */}
              <div className="flex items-center gap-6 pt-2">
                <div className="flex items-center gap-2">
                  <Switch
                    id="most-recent"
                    checked={filters.mostRecentPerAccount}
                    onCheckedChange={(checked) => setFilters(prev => ({ ...prev, mostRecentPerAccount: checked }))}
                  />
                  <Label htmlFor="most-recent" className="cursor-pointer">
                    Most Recent Bill per Account
                  </Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    id="include-deleted"
                    checked={filters.includeDeleted}
                    onCheckedChange={(checked) => setFilters(prev => ({ ...prev, includeDeleted: checked }))}
                  />
                  <Label htmlFor="include-deleted" className="cursor-pointer">
                    Include Deleted Bills
                  </Label>
                </div>
              </div>

              {/* Clear Button */}
              <div className="flex justify-end pt-2">
                <Button variant="outline" onClick={clearFilters}>
                  Clear
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Bills Summary and Actions */}
      <Card className="bg-gradient-to-r from-primary/10 via-primary/5 to-background border-primary/20">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-8">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-background rounded-lg">
                  <FileText className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground font-medium">Total Records</p>
                  <p className="text-3xl font-bold">{totalBills}</p>
                </div>
              </div>
              {selectedBills.size > 0 && (
                <>
                  <Separator orientation="vertical" className="h-12" />
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-primary/20 rounded-lg">
                      <CheckCircle className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground font-medium">Selected Items</p>
                      <p className="text-3xl font-bold text-primary">{selectedBills.size}</p>
                    </div>
                  </div>
                </>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleSelectAll}
                className="gap-2"
              >
                {selectedBills.size === bills.length && bills.length > 0 ? (
                  <>
                    <X className="h-4 w-4" />
                    Deselect All
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-4 w-4" />
                    Select All
                  </>
                )}
              </Button>
              {selectedBills.size > 0 && (
                <Button
                  size="sm"
                  onClick={() => {
                    // Bulk download logic
                  }}
                  className="gap-2"
                >
                  <Download className="h-4 w-4" />
                  Download Selected ({selectedBills.size})
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Bills Table */}
      <Card className="border-l-4 border-l-primary/50">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Receipt className="h-5 w-5 text-primary" />
            </div>
            <div>
              <CardTitle className="text-lg">Invoice Records</CardTitle>
              <CardDescription>View and manage all utility bill invoices</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader className="bg-muted/30">
                <TableRow>
                  <TableHead className="w-12">
                    <Checkbox
                      checked={selectedBills.size === bills.length && bills.length > 0}
                      onCheckedChange={handleSelectAll}
                    />
                  </TableHead>
                  <SortableHeader field="vendor" icon={<Building className="h-4 w-4" />}>
                    <span>Vendor</span>
                    <Info className="h-3 w-3 text-muted-foreground" />
                  </SortableHeader>
                  <SortableHeader field="bill_date" icon={<Calendar className="h-4 w-4" />}>
                    Invoice Date
                  </SortableHeader>
                  <SortableHeader field="bill_number" icon={<CreditCard className="h-4 w-4" />}>
                    Billing ID
                  </SortableHeader>
                  <SortableHeader field="billing_period_start" icon={<Calendar className="h-4 w-4" />}>
                    Service Start
                  </SortableHeader>
                  <SortableHeader field="billing_period_end" icon={<Calendar className="h-4 w-4" />}>
                    Service End
                  </SortableHeader>
                  <SortableHeader field="amount" icon={<DollarSign className="h-4 w-4" />}>
                    Current Charges
                  </SortableHeader>
                  <SortableHeader field="amount_due" icon={<DollarSign className="h-4 w-4" />}>
                    Total Due
                  </SortableHeader>
                  <TableHead>
                    <div className="flex items-center gap-2 font-semibold">
                      <Wallet className="h-4 w-4 text-muted-foreground" />
                      Pay Amount
                    </div>
                  </TableHead>
                  <SortableHeader field="due_date" icon={<Calendar className="h-4 w-4" />}>
                    Payment Due
                  </SortableHeader>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={11} className="text-center py-12">
                      <div className="flex flex-col items-center gap-2">
                        <Clock className="h-8 w-8 text-muted-foreground animate-spin" />
                        <p className="text-muted-foreground">Loading bills...</p>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : bills.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} className="text-center py-12">
                      <div className="flex flex-col items-center gap-2">
                        <FileText className="h-12 w-12 text-muted-foreground/50" />
                        <p className="text-muted-foreground font-medium">No bills found</p>
                        <p className="text-sm text-muted-foreground">Adjust your filters or upload bills to get started</p>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  bills.map((bill: any) => (
                    <TableRow
                      key={bill.id}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => {
                        const billId = bill._isDemo ? 'demo' : bill.id;
                        if (currentWorkspace?.id) {
                          navigate(`/w/${currentWorkspace.id}/dashboard/tum/bills/${billId}`);
                        } else {
                          navigate(`/tum/bills/${billId}`);
                        }
                      }}
                    >
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={selectedBills.has(bill.id)}
                          onCheckedChange={() => handleSelectBill(bill.id)}
                        />
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Building className="h-4 w-4 text-muted-foreground" />
                          <span className="font-medium">{bill.utility_account?.vendor?.vendor_name || 'N/A'}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                          <span>{formatDate(bill.bill_date)}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <CreditCard className="h-3.5 w-3.5 text-muted-foreground" />
                          <span className="font-mono text-sm">{bill.bill_number || bill.id.slice(0, 8)}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                          <span className="text-sm">{formatDate(bill.billing_period_start)}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                          <span className="text-sm">{formatDate(bill.billing_period_end)}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <DollarSign className="h-3.5 w-3.5 text-muted-foreground" />
                          <span className="font-semibold">{formatCurrency(bill.amount)}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <DollarSign className="h-3.5 w-3.5 text-primary" />
                          <span className="font-semibold text-primary">{formatCurrency(bill.amount_due || bill.amount)}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Wallet className="h-3.5 w-3.5 text-muted-foreground" />
                          <span className="font-medium">{formatCurrency(bill.amount_due || bill.amount)}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                          <span className="text-sm">{formatDate(bill.due_date)}</span>
                        </div>
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                          <Eye className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Pagination */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <div className="flex items-center gap-1">
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const page = i + 1;
              return (
                <Button
                  key={page}
                  variant={currentPage === page ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setCurrentPage(page)}
                >
                  {page}
                </Button>
              );
            })}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Go to</span>
          <Input
            type="number"
            min={1}
            max={totalPages}
            value={currentPage}
            onChange={(e) => {
              const page = parseInt(e.target.value);
              if (page >= 1 && page <= totalPages) {
                setCurrentPage(page);
              }
            }}
            className="w-16"
          />
          <Select value={pageSize.toString()} onValueChange={(value) => setPageSize(parseInt(value))}>
            <SelectTrigger className="w-24">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="10">10/page</SelectItem>
              <SelectItem value="20">20/page</SelectItem>
              <SelectItem value="50">50/page</SelectItem>
              <SelectItem value="100">100/page</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
}

